import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackMenuView } from "../utils/analytics";
import "../styles/MenuPage.css";

import MenuDisplay from "../components/MenuDisplay";
import MenuCategoryLink from "../components/MenuCategoryLink";
import LocationHero from "../components/LocationHero";
import {
  menuConfigs,
  getNavSections,
  getSectionLabel,
  Location,
} from "../data/menuConfig";

interface MenuPageProps {
  location: Location;
  section: string;
}

const MenuPage: React.FC<MenuPageProps> = ({ location, section }) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as "en" | "fr";

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  // Track menu view on mount and when section changes
  useEffect(() => {
    trackMenuView(location, section);
  }, [location, section]);

  // Close accordion when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        accordionRef.current &&
        !accordionRef.current.contains(event.target as Node)
      ) {
        setIsAccordionOpen(false);
      }
    };

    if (isAccordionOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAccordionOpen]);

  // Get navigation sections from config
  const navSections = getNavSections(location);

  // Get current section config and submenus
  const currentSectionConfig = menuConfigs[location][section];
  const currentSubmenus = currentSectionConfig?.submenus || [];

  useEffect(() => {
    // Scroll to the menu content section on mount with offset for sticky header
    const menuSection = document.querySelector(".menu-category-section");
    if (menuSection) {
      // Dynamically get header height
      const header = document.querySelector(".header");
      const headerHeight = header ? header.getBoundingClientRect().height : 60;

      // Add extra padding based on screen size
      const extraPadding = window.innerWidth <= 768 ? 100 : 120;

      const elementPosition = menuSection.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerHeight - extraPadding;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }, [section]);

  const locationData = {
    montreal: {
      name: "MONTRÉAL",
      hours: t("locations.montreal.hours"),
      address:
        currentLang === "fr"
          ? "2532 rue Ste-Catherine E\nMontréal, QC H2K 2K2"
          : "2532 St Catherine St E\nMontreal, QC H2K 2K2",
      phone: "514-527-2532",
      phoneLink: "tel:5145272532",
    },
    delson: {
      name: "DELSON",
      hours: t("locations.delson.hours"),
      address: "35 Boulevard Georges-Gagné Sud\nDelson, QC J5B 2E4",
      phone: "450-632-5320",
      phoneLink: "tel:4506325320",
    },
  };

  const data = locationData[location];
  const locationName = data.name.toLocaleLowerCase();
  const locationNameCamelCase =
    locationName.charAt(0).toUpperCase() + locationName.slice(1);

  return (
    <div className="menu-page">
      {/* Hero Section */}
      <LocationHero
        locationName={data.name}
        description={t("location.description", {
          location: locationNameCamelCase,
        })}
        hours={data.hours}
        address={data.address}
        phone={data.phone}
        phoneLink={data.phoneLink}
        variant="menu"
      />

      {/* Menu Section */}
      <section className="menu-section">
        <div className="container">
          <h2 className="menu-title">{t("menu.title")}</h2>

          {/* Mobile Accordion */}
          <div className="menu-accordion-toggle-wrapper" ref={accordionRef}>
            <button
              className="menu-accordion-toggle"
              onClick={toggleAccordion}
              aria-expanded={isAccordionOpen}
              aria-label={t("common.toggleMenuCategories")}
            >
              <span className="menu-accordion-current">
                {getSectionLabel(location, section, currentLang)}
              </span>
              <span
                className={`menu-accordion-icon ${isAccordionOpen ? "open" : ""}`}
              >
                ▼
              </span>
            </button>

            {/* Mobile Accordion Dropdown */}
            {isAccordionOpen && (
              <div className="menu-accordion-dropdown">
                {navSections.map((sec) => (
                  <Link
                    key={sec}
                    to={`/${location}/menu/${sec}`}
                    className={`menu-accordion-link ${section === sec ? "active" : ""}`}
                    onClick={() => setIsAccordionOpen(false)}
                  >
                    {getSectionLabel(location, sec, currentLang)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="menu-categories-nav">
            {navSections.map((sec, index) => (
              <MenuCategoryLink
                key={sec}
                to={`/${location}/menu/${sec}`}
                isActive={section === sec}
                variant="menu"
                showDivider={index < navSections.length - 1}
              >
                {getSectionLabel(location, sec, currentLang)}
              </MenuCategoryLink>
            ))}
          </div>

          {/* Menu Display - Uses submenus from config */}
          {currentSectionConfig && (
            <div key={section} className="menu-category-section">
              <MenuDisplay
                location={location}
                section={section}
                submenus={[...currentSubmenus]}
                language={currentLang}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
